import Service, { inject as service } from '@ember/service';
import { computed, set } from '@ember/object';
import { task, dropTask } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import CampaignModel from 'cio/models/campaign';
import { CampaignSubscription } from 'cio/models/user';

export default class CampaignSubscriptionsService extends Service {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;

  @computed('environment.current.id', 'allSubscriptions.[]')
  get subscribedCampaigns() {
    if (!this.environment.current.id) {
      return [];
    }

    let envId = parseInt(this.environment.current.id);

    return this.allSubscriptions
      .filter((subscription) => subscription.environment_id === envId)
      .map((subscription) => subscription.campaign_id);
  }

  allSubscriptions: CampaignSubscription[] = [];

  getSubscriptions = task(async () => {
    // If the processTask is running, we don't want to enqueue it again.
    //
    // It will get dropped and if the task is being used as a promise, i.e.
    // with a "then", it will throw an error. It's fine if the error is expected
    // and handled, but this breaks within a route's lifecycle hooks.
    //
    // For example, if `_getSubscriptions` is running and a route's afterModel tries
    // to create it again, the task will be dropped and an error will be thrown
    if (this._getSubscriptions.isRunning) {
      return await this._getSubscriptions.last;
    }

    return this._getSubscriptions.perform();
  });

  _getSubscriptions = dropTask(async () => {
    if (this.auth.user == null) {
      throw new Error('Unable to find current user. Refresh the page and try again.');
    }

    let subscriptions = await this.auth.user.getSubscriptions({});
    set(this, 'allSubscriptions', subscriptions);
  });

  subscribe = dropTask(async (campaign: CampaignModel) => {
    if (this.auth.user == null) {
      throw new Error('Unable to find current user. Refresh the page and try again.');
    }

    await this.auth.user.followCampaign({
      campaignId: campaign.id,
      environmentId: this.environment.current.id,
    });
    return this.getSubscriptions.perform();
  });

  unsubscribe = dropTask(async (campaign: CampaignModel) => {
    if (this.auth.user == null) {
      throw new Error('Unable to find current user. Refresh the page and try again.');
    }

    await this.auth.user.unfollowCampaign({
      campaignId: campaign.id,
      environmentId: this.environment.current.id,
    });
    return this.getSubscriptions.perform();
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'campaign-subscriptions': CampaignSubscriptionsService;
  }
}
